import { api } from '@/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface TestResultData {
  id: string;
  version: string;
  featureIndex: number;
  featureName: string;
  status: 'passed' | 'failed' | 'not_tested';
  comment: string | null;
  stepResults: string | null;
  testerName: string;
  testerId: string;
  testedAt: string;
  updatedAt: string;
  historyCount: number;
}

interface TestResultsResponse {
  results: TestResultData[];
}

interface SaveTestResultPayload {
  version: string;
  featureIndex: number;
  featureName: string;
  status: 'passed' | 'failed' | 'not_tested';
  comment?: string | null;
  stepResults?: Record<number, boolean> | null;
}

export const testResultKeys = {
  all: ['release-hub', 'test-results'] as const,
  byVersion: (version?: string) => [...testResultKeys.all, version ?? 'all'] as const,
};

export function useTestResults(version?: string) {
  const params = version ? `?version=${version}` : '';
  return useQuery<TestResultsResponse, Error, TestResultData[]>({
    queryKey: testResultKeys.byVersion(version),
    queryFn: () => api.get<TestResultsResponse>(`/release-hub/test-results${params}`),
    select: response => response.results,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSaveTestResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveTestResultPayload) =>
      api.post<{ success: boolean; result: TestResultData }>('/release-hub/test-results', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: testResultKeys.all });
    },
  });
}

interface RetestPayload {
  version: string;
  featureIndex: number;
  retestNote?: string | undefined;
}

export function useRetestFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RetestPayload) =>
      api.post<{ archived: boolean; result?: TestResultData }>(
        '/release-hub/test-results/retest',
        payload
      ),
    onSuccess: (_data, payload) => {
      void queryClient.invalidateQueries({ queryKey: testResultKeys.all });
      void queryClient.invalidateQueries({
        queryKey: ['release-hub', 'test-history', payload.version, payload.featureIndex],
      });
    },
  });
}

export function useExportTestResultsCsv(version?: string) {
  const params = version ? `?version=${version}` : '';
  return {
    exportUrl: `/api/v1/release-hub/test-results/export${params}`,
  };
}

export interface TestResultHistoryItem {
  id: string;
  version: string;
  featureIndex: number;
  featureName: string;
  status: 'passed' | 'failed' | 'not_tested';
  comment: string | null;
  stepResults: string | null;
  testerName: string;
  retestNote: string | null;
  testedAt: string;
  archivedAt: string;
}

interface TestResultHistoryResponse {
  history: TestResultHistoryItem[];
}

export function useTestHistory(version: string, featureIndex: number, enabled: boolean) {
  return useQuery<TestResultHistoryResponse, Error, TestResultHistoryItem[]>({
    queryKey: ['release-hub', 'test-history', version, featureIndex],
    queryFn: () =>
      api.get<TestResultHistoryResponse>(
        `/release-hub/test-results/history?version=${encodeURIComponent(version)}&featureIndex=${featureIndex}`
      ),
    select: response => response.history,
    enabled,
    staleTime: 30 * 1000,
  });
}
